import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { intelligenceAPI, coreAPI, suppliersAPI, fetchAllPages, QualificationTemplate } from '../lib/api';
import type { SupplierTargeting, CompanyGroup, Site, SupplierProfile } from '../lib/api';
import { useViewKey } from '../contexts/ViewContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  ArrowLeft,
  Save,
  SendHorizonal,
  Plus,
  Trash2,
  Loader2,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Settings2,
  ListChecks,
  Target,
  Eye,
  Layers,
  CalendarClock,
  RefreshCw,
  UserPlus,
  Users,
  Building2,
  User,
  Package,
  Check,
  X,
  Search,
  Star,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/errorUtils';
import { useIsSupplier } from '../hooks/usePortalMode';
import TemplatePreview from '../components/questionnaire/TemplatePreview';
import { QuestionEditor } from './QuestionEditor';
import {
  LocalQuestion,
  LocalSection,
  TEMPLATE_TYPES,
  RECURRENCE_FREQUENCIES,
  SECTION_COLORS,
  makeQuestion,
  makeSection,
  sectionFromApi,
  getChoiceQuestionErrors,
  MIN_CHOICES_PER_QUESTION,
} from './templateBuilderUtils';
import type { RecurrenceFrequency } from '../lib/api';

export default function TemplateBuilder() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  // Template settings
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState<QualificationTemplate['template_type']>('custom');
  const [passScore, setPassScore] = useState(70);
  const [instructions, setInstructions] = useState('');
  const [autoCalculateScore, setAutoCalculateScore] = useState(true);
  const [requiresApproval, setRequiresApproval] = useState(true);
  const [allowAttachments, setAllowAttachments] = useState(true);
  const [templateStatus, setTemplateStatus] = useState<QualificationTemplate['status']>('draft');

  // Onboarding & Recurrence settings
  const [isMandatoryForOnboarding, setIsMandatoryForOnboarding] = useState(false);
  const [blocksSupplierApproval, setBlocksSupplierApproval] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceFrequency, setRecurrenceFrequency] = useState<RecurrenceFrequency>('one_time');
  const [customIntervalDays, setCustomIntervalDays] = useState<number | undefined>(undefined);
  const [validityPeriodDays, setValidityPeriodDays] = useState<number | undefined>(undefined);
  const [renewalReminderDays, setRenewalReminderDays] = useState(30);

  // Supplier Targeting settings
  const [targetType, setTargetType] = useState<SupplierTargeting['target_type']>('all');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([]);
  const [selectedSiteIds, setSelectedSiteIds] = useState<number[]>([]);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState<number[]>([]);
  const [commodityCodes, setCommodityCodes] = useState<string>('');

  // Supplier Targeting options (loaded from API)
  const [availableGroups, setAvailableGroups] = useState<CompanyGroup[]>([]);
  const [availableSites, setAvailableSites] = useState<Site[]>([]);
  const [availableSuppliers, setAvailableSuppliers] = useState<SupplierProfile[]>([]);
  const [loadingTargetOptions, setLoadingTargetOptions] = useState(false);
  const [targetSearchTerm, setTargetSearchTerm] = useState('');

  // Sections
  const [sections, setSections] = useState<LocalSection[]>([makeSection()]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [templateId, setTemplateId] = useState<number | null>(id ? parseInt(id, 10) : null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [previewOpen, setPreviewOpen] = useState(false);

  // CR-5918: once a save/publish has been blocked, surface missing answer
  // options as a persistent banner + inline question errors that recompute live
  // from `sections`, so the feedback clears itself as the user fixes each one.
  const [showChoiceErrors, setShowChoiceErrors] = useState(false);
  const choiceErrors = showChoiceErrors ? getChoiceQuestionErrors(sections) : [];
  const invalidQuestionKeys = new Set(choiceErrors.map((e) => e.questionKey));

  // CR-7111: Suppliers should not access template builder pages directly via URL
  const isSupplier = useIsSupplier();
  useEffect(() => {
    if (isSupplier) {
      navigate('/questionnaires', { replace: true });
    }
  }, [isSupplier, navigate]);

  // Load existing template in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      loadTemplate(parseInt(id, 10));
    }
  }, [id, isEditMode]);

  // Default all sections to open
  useEffect(() => {
    const open: Record<string, boolean> = {};
    sections.forEach((s) => { open[s._key] = openSections[s._key] !== false; });
    setOpenSections(open);
  }, [sections.length]);

  // Load supplier targeting options
  const viewKey = useViewKey();
  useEffect(() => {
    const loadTargetingOptions = async () => {
      setLoadingTargetOptions(true);
      try {
        const [groupsRes, sites, suppliersRes] = await Promise.all([
          coreAPI.getSupplierGroupsForFilter(),
          // Walk pages — the backend clamps page_size, so one request silently truncated.
          fetchAllPages<Site>((page) => coreAPI.getSites({ page_size: 100, page })),
          suppliersAPI.getProfiles({ page_size: 500 }),
        ]);
        setAvailableGroups(groupsRes.data.results || []);
        setAvailableSites(sites);
        setAvailableSuppliers(suppliersRes.data.results || []);
      } catch (err) {
        console.error('Error loading targeting options:', err);
      } finally {
        setLoadingTargetOptions(false);
      }
    };
    loadTargetingOptions();
  }, [viewKey]); // supplier-group options follow the site switcher

  const loadTemplate = async (templateIdToLoad: number) => {
    try {
      setLoading(true);
      const response = await intelligenceAPI.getTemplate(templateIdToLoad);
      const t = response.data;

      setName(t.name);
      setDescription(t.description || '');
      setTemplateType(t.template_type);
      setPassScore(t.pass_score);
      setInstructions(t.instructions || '');
      setAutoCalculateScore(t.auto_calculate_score);
      setRequiresApproval(t.requires_approval);
      setAllowAttachments(t.allow_attachments);
      setTemplateStatus(t.status);
      setTemplateId(t.id);
      // Load onboarding & recurrence settings
      setIsMandatoryForOnboarding(t.is_mandatory_for_onboarding || false);
      setBlocksSupplierApproval(t.blocks_supplier_approval || false);
      setIsRecurring(t.is_recurring || false);
      setRecurrenceFrequency(t.recurrence_frequency || 'one_time');
      setCustomIntervalDays(t.custom_interval_days ?? undefined);
      setValidityPeriodDays(t.validity_period_days ?? undefined);
      setRenewalReminderDays(t.renewal_reminder_days || 30);

      // Load supplier targeting settings
      if (t.supplier_targeting) {
        setTargetType(t.supplier_targeting.target_type || 'all');
        setSelectedGroupIds(t.supplier_targeting.group_ids || []);
        setSelectedSiteIds(t.supplier_targeting.site_ids || []);
        setSelectedSupplierIds(t.supplier_targeting.supplier_ids || []);
        setCommodityCodes((t.supplier_targeting.commodity_codes || []).join(', '));
      }

      if (t.sections && t.sections.length > 0) {
        const sorted = [...t.sections].sort((a, b) => a.sort_order - b.sort_order);
        setSections(sorted.map(sectionFromApi));
      } else {
        setSections([makeSection()]);
      }
    } catch (err: unknown) {
      console.error('Error loading template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to load template'));
      navigate('/questionnaires?tab=templates');
    } finally {
      setLoading(false);
    }
  };

  // -- Section helpers --
  const addSection = useCallback(() => {
    setSections((prev) => [...prev, makeSection()]);
  }, []);

  const removeSection = useCallback((key: string) => {
    setSections((prev) => prev.length > 1 ? prev.filter((s) => s._key !== key) : prev);
  }, []);

  const updateSection = useCallback((key: string, field: keyof LocalSection, value: unknown) => {
    setSections((prev) =>
      prev.map((s) => (s._key === key ? { ...s, [field]: value } : s))
    );
  }, []);

  const moveSectionUp = useCallback((idx: number) => {
    if (idx === 0) return;
    setSections((prev) => {
      const arr = [...prev];
      [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
      return arr;
    });
  }, []);

  const moveSectionDown = useCallback((idx: number) => {
    setSections((prev) => {
      if (idx >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
      return arr;
    });
  }, []);

  // -- Question helpers --
  const addQuestion = useCallback((sectionKey: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s._key === sectionKey
          ? { ...s, questions: [...s.questions, makeQuestion()] }
          : s
      )
    );
  }, []);

  const removeQuestion = useCallback((sectionKey: string, questionKey: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s._key === sectionKey
          ? { ...s, questions: s.questions.filter((q) => q._key !== questionKey) }
          : s
      )
    );
  }, []);

  const updateQuestion = useCallback(
    (sectionKey: string, questionKey: string, field: keyof LocalQuestion, value: unknown) => {
      setSections((prev) =>
        prev.map((s) =>
          s._key === sectionKey
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q._key === questionKey ? { ...q, [field]: value } : q
                ),
              }
            : s
        )
      );
    },
    []
  );

  const moveQuestionUp = useCallback((sectionKey: string, qIdx: number) => {
    if (qIdx === 0) return;
    setSections((prev) =>
      prev.map((s) => {
        if (s._key !== sectionKey) return s;
        const qs = [...s.questions];
        [qs[qIdx - 1], qs[qIdx]] = [qs[qIdx], qs[qIdx - 1]];
        return { ...s, questions: qs };
      })
    );
  }, []);

  const moveQuestionDown = useCallback((sectionKey: string, qIdx: number) => {
    setSections((prev) =>
      prev.map((s) => {
        if (s._key !== sectionKey) return s;
        if (qIdx >= s.questions.length - 1) return s;
        const qs = [...s.questions];
        [qs[qIdx], qs[qIdx + 1]] = [qs[qIdx + 1], qs[qIdx]];
        return { ...s, questions: qs };
      })
    );
  }, []);

  // CR-5918: gate save/publish on choice questions having answer options.
  // Returns true (and surfaces persistent inline errors) when blocked. The
  // banner/inline messages are rendered from `showChoiceErrors`, never as a
  // toast-only message, because a transient toast was missed by users entirely.
  const checkChoiceErrors = (): boolean => {
    const errs = getChoiceQuestionErrors(sections);
    if (errs.length === 0) {
      setShowChoiceErrors(false);
      return false;
    }
    setShowChoiceErrors(true);
    // Reveal any collapsed section that contains an error so its inline message
    // is visible without the user having to hunt for it.
    setOpenSections((prev) => {
      const next = { ...prev };
      errs.forEach((e) => {
        const key = sections[e.sectionIdx]?._key;
        if (key) next[key] = true;
      });
      return next;
    });
    return true;
  };

  // -- Save --
  const handleSave = async (): Promise<number | undefined> => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return undefined;
    }

    // CR-5918: block save when Single/Multiple Choice questions lack options.
    if (checkChoiceErrors()) return undefined;

    try {
      setSaving(true);
      let currentId = templateId;

      // Build supplier targeting configuration
      const supplierTargeting: SupplierTargeting = {
        target_type: targetType,
      };
      if (targetType === 'groups' && selectedGroupIds.length > 0) {
        supplierTargeting.group_ids = selectedGroupIds;
      }
      if (targetType === 'sites' && selectedSiteIds.length > 0) {
        supplierTargeting.site_ids = selectedSiteIds;
      }
      if (targetType === 'suppliers' && selectedSupplierIds.length > 0) {
        supplierTargeting.supplier_ids = selectedSupplierIds;
      }
      if (commodityCodes.trim()) {
        supplierTargeting.commodity_codes = commodityCodes.split(',').map(c => c.trim()).filter(Boolean);
      }

      const templateData: Partial<QualificationTemplate> = {
        name: name.trim(),
        description: description.trim(),
        template_type: templateType,
        pass_score: passScore,
        instructions: instructions.trim(),
        auto_calculate_score: autoCalculateScore,
        requires_approval: requiresApproval,
        allow_attachments: allowAttachments,
        // Onboarding & Recurrence settings
        is_mandatory_for_onboarding: isMandatoryForOnboarding,
        blocks_supplier_approval: blocksSupplierApproval,
        is_recurring: isRecurring,
        recurrence_frequency: recurrenceFrequency,
        custom_interval_days: recurrenceFrequency === 'custom' ? customIntervalDays : undefined,
        validity_period_days: validityPeriodDays,
        renewal_reminder_days: renewalReminderDays,
        // Supplier targeting
        supplier_targeting: supplierTargeting,
      };

      if (!currentId) {
        const res = await intelligenceAPI.createTemplate(templateData);
        currentId = res.data.id;
        setTemplateId(currentId);
      } else {
        await intelligenceAPI.updateTemplate(currentId, templateData);
      }

      const sectionsPayload = sections.map((s) => ({
        id: s.id,
        title: s.title.trim() || 'Untitled Section',
        description: s.description.trim(),
        weight: s.weight,
        is_required: s.is_required,
        questions: s.questions.map((q) => ({
          id: q.id,
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          help_text: q.help_text.trim(),
          is_required: q.is_required,
          is_scored: q.is_scored,
          max_score: q.max_score,
          scoring_criteria: q.scoring_criteria,
          choices: q.choices,
        })),
      }));

      // CR-4797: rehydrate local state from the server's response so the
      // builder reflects exactly what was persisted, not the in-memory draft.
      // Previously the UI kept the pre-save state even if the backend
      // coerced or dropped any field — users then reopened the record and
      // saw different data, which reads as "Save Draft didn't work".
      const saveResp = await intelligenceAPI.saveTemplateStructure(currentId, sectionsPayload);
      const saved = saveResp.data?.template;
      if (saved?.sections && saved.sections.length > 0) {
        const sorted = [...saved.sections].sort((a, b) => a.sort_order - b.sort_order);
        setSections(sorted.map(sectionFromApi));
      }
      toast.success('Template saved successfully');

      if (!isEditMode) {
        navigate(`/intelligence/templates/${currentId}`, { replace: true });
      }
      return currentId;
    } catch (err: unknown) {
      console.error('Error saving template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to save template'));
      return undefined;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    // CR-5918: block publish when Single/Multiple Choice questions lack options,
    // ahead of the save-first check, so the inline answer-option errors surface
    // immediately even on a brand-new template the user has not saved yet.
    if (checkChoiceErrors()) return;

    try {
      setPublishing(true);
      // Publish used to refuse on an unsaved template and tell the user to
      // save first — but it saves anyway as its first step, so the refusal
      // was busywork. Save, then publish whatever id that produced.
      const savedId = await handleSave();
      const idToPublish = templateId ?? savedId;
      if (!idToPublish) {
        // handleSave already surfaced why it could not save.
        return;
      }
      await intelligenceAPI.publishTemplate(idToPublish);
      setTemplateStatus('active');
      toast.success('Template published successfully');
    } catch (err: unknown) {
      console.error('Error publishing template:', err);
      toast.error(getApiErrorMessage(err, 'Failed to publish template'));
    } finally {
      setPublishing(false);
    }
  };

  // -- Choice management --
  const addChoice = useCallback((sectionKey: string, questionKey: string) => {
    setSections((prev) =>
      prev.map((s) =>
        s._key === sectionKey
          ? {
              ...s,
              questions: s.questions.map((q) =>
                q._key === questionKey ? { ...q, choices: [...q.choices, ''] } : q
              ),
            }
          : s
      )
    );
  }, []);

  const updateChoice = useCallback(
    (sectionKey: string, questionKey: string, choiceIdx: number, value: string) => {
      setSections((prev) =>
        prev.map((s) =>
          s._key === sectionKey
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q._key === questionKey
                    ? {
                        ...q,
                        choices: q.choices.map((c, i) => (i === choiceIdx ? value : c)),
                      }
                    : q
                ),
              }
            : s
        )
      );
    },
    []
  );

  const removeChoice = useCallback(
    (sectionKey: string, questionKey: string, choiceIdx: number) => {
      setSections((prev) =>
        prev.map((s) =>
          s._key === sectionKey
            ? {
                ...s,
                questions: s.questions.map((q) =>
                  q._key === questionKey
                    ? { ...q, choices: q.choices.filter((_, i) => i !== choiceIdx) }
                    : q
                ),
              }
            : s
        )
      );
    },
    []
  );

  const toggleSection = useCallback((key: string) => {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
          <p className="text-muted-foreground">Loading template...</p>
        </div>
      </div>
    );
  }

  const totalQuestions = sections.reduce((sum, s) => sum + s.questions.length, 0);
  const totalMaxScore = sections.reduce(
    (sum, s) => sum + s.questions.reduce((qSum, q) => qSum + (q.is_scored ? Number(q.max_score) || 0 : 0), 0),
    0
  );

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gray-50/50">
        {/* Sticky Header */}
        <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/questionnaires?tab=templates')} className="rounded-full">
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-bold text-gray-900">
                      {isEditMode ? 'Edit Template' : 'New Template'}
                    </h1>
                    {templateStatus === 'active' && (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>
                    )}
                    {templateStatus === 'draft' && (
                      <Badge variant="secondary" className="bg-amber-100 text-amber-700 hover:bg-amber-100">Draft</Badge>
                    )}
                    {templateStatus === 'archived' && (
                      <Badge variant="outline">Archived</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-4 mt-0.5">
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Layers className="h-3.5 w-3.5" />
                      {sections.length} section{sections.length !== 1 ? 's' : ''}
                    </span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <ListChecks className="h-3.5 w-3.5" />
                      {totalQuestions} question{totalQuestions !== 1 ? 's' : ''}
                    </span>
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Target className="h-3.5 w-3.5" />
                      {totalMaxScore} pts max
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setPreviewOpen(true)}>
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button variant="outline" onClick={handleSave} disabled={saving || publishing}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  {saving ? 'Saving...' : 'Save Draft'}
                </Button>
                {templateStatus === 'draft' && (
                  <Button onClick={handlePublish} disabled={saving || publishing} className="bg-emerald-600 hover:bg-emerald-700">
                    {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SendHorizonal className="h-4 w-4 mr-2" />}
                    {publishing ? 'Publishing...' : 'Publish'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
          {/* CR-5918: persistent validation summary — answer options missing */}
          {choiceErrors.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3" role="alert">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-semibold">
                    Add answer options before saving or publishing
                  </p>
                  <ul className="mt-1 list-disc pl-5 space-y-0.5">
                    {choiceErrors.map((e) => (
                      <li key={e.questionKey}>{e.message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Template Settings */}
          <Card className="shadow-sm border-gray-200">
            <div className="px-6 py-4 border-b bg-gray-50/80 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Settings2 className="h-5 w-5 text-gray-500" />
                <h2 className="text-base font-semibold text-gray-900">Template Settings</h2>
              </div>
            </div>
            <CardContent className="p-6 space-y-6">
              {/* Row 1: Name + Type */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="template-name" className="text-sm font-medium">
                    Template Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="template-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., ISO 9001 Compliance Assessment"
                    className="h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-type" className="text-sm font-medium">Type</Label>
                  <Select value={templateType} onValueChange={(v) => setTemplateType(v as QualificationTemplate['template_type'])}>
                    <SelectTrigger id="template-type" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEMPLATE_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Row 2: Description */}
              <div className="space-y-2">
                <Label htmlFor="template-desc" className="text-sm font-medium">Description</Label>
                <Textarea
                  id="template-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of this template's purpose..."
                  rows={2}
                />
              </div>

              {/* Row 3: Instructions */}
              <div className="space-y-2">
                <Label htmlFor="template-instructions" className="text-sm font-medium">
                  Instructions for Suppliers
                </Label>
                <Textarea
                  id="template-instructions"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Instructions that will be shown to suppliers when filling out this template..."
                  rows={3}
                />
              </div>

              {/* Row 4: Pass Score + Options */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="pass-score" className="text-sm font-medium">Pass Score (%)</Label>
                  <Input
                    id="pass-score"
                    type="number"
                    min={0}
                    max={100}
                    value={passScore}
                    onChange={(e) => setPassScore(parseInt(e.target.value, 10) || 0)}
                    className="h-10 w-32"
                  />
                </div>
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Options</Label>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                      <div className="space-y-0.5">
                        <Label htmlFor="auto-score" className="text-sm font-medium cursor-pointer">Auto-calculate score</Label>
                        <p className="text-xs text-muted-foreground">Automatically calculate scores from question weights</p>
                      </div>
                      <Switch id="auto-score" checked={autoCalculateScore} onCheckedChange={setAutoCalculateScore} />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                      <div className="space-y-0.5">
                        <Label htmlFor="requires-approval" className="text-sm font-medium cursor-pointer">Requires approval</Label>
                        <p className="text-xs text-muted-foreground">Submissions need manager approval</p>
                      </div>
                      <Switch id="requires-approval" checked={requiresApproval} onCheckedChange={setRequiresApproval} />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                      <div className="space-y-0.5">
                        <Label htmlFor="allow-attachments" className="text-sm font-medium cursor-pointer">Allow attachments</Label>
                        <p className="text-xs text-muted-foreground">Suppliers can upload supporting documents</p>
                      </div>
                      <Switch id="allow-attachments" checked={allowAttachments} onCheckedChange={setAllowAttachments} />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lifecycle & Recurrence Configuration */}
          <Card className="shadow-sm border-gray-200">
            <div className="px-6 py-4 border-b bg-gray-50/80 rounded-t-lg">
              <div className="flex items-center gap-2">
                <CalendarClock className="h-5 w-5 text-gray-500" />
                <h2 className="text-base font-semibold text-gray-900">Lifecycle & Recurrence</h2>
              </div>
            </div>
            <CardContent className="p-6 space-y-6">
              {/* Onboarding Configuration */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Onboarding Configuration</Label>
                </div>
                <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                  <div className="space-y-0.5">
                    <Label htmlFor="mandatory-onboarding" className="text-sm font-medium cursor-pointer">
                      Mandatory for Onboarding
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Questionnaire must be completed as part of supplier onboarding
                    </p>
                  </div>
                  <Switch
                    id="mandatory-onboarding"
                    checked={isMandatoryForOnboarding}
                    onCheckedChange={setIsMandatoryForOnboarding}
                  />
                </div>
                {isMandatoryForOnboarding && (
                  <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white ml-4 border-l-2 border-l-primary/30">
                    <div className="space-y-0.5">
                      <Label htmlFor="blocks-approval" className="text-sm font-medium cursor-pointer">
                        Blocks Supplier Approval
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Supplier cannot be approved until this questionnaire is completed and approved
                      </p>
                    </div>
                    <Switch
                      id="blocks-approval"
                      checked={blocksSupplierApproval}
                      onCheckedChange={setBlocksSupplierApproval}
                    />
                  </div>
                )}
              </div>

              {/* Recurrence Configuration */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Recurrence Configuration</Label>
                </div>
                <div className="flex items-center justify-between rounded-lg border px-4 py-3 bg-white">
                  <div className="space-y-0.5">
                    <Label htmlFor="is-recurring" className="text-sm font-medium cursor-pointer">
                      Enable Recurring
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically reissue questionnaire based on frequency
                    </p>
                  </div>
                  <Switch
                    id="is-recurring"
                    checked={isRecurring}
                    onCheckedChange={setIsRecurring}
                  />
                </div>

                {isRecurring && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4 border-l-2 border-primary/20">
                    <div className="space-y-2">
                      <Label htmlFor="recurrence-frequency" className="text-sm font-medium">
                        Frequency
                      </Label>
                      <Select
                        value={recurrenceFrequency}
                        onValueChange={(v) => setRecurrenceFrequency(v as RecurrenceFrequency)}
                      >
                        <SelectTrigger id="recurrence-frequency" className="h-10">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RECURRENCE_FREQUENCIES.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {recurrenceFrequency === 'custom' && (
                      <div className="space-y-2">
                        <Label htmlFor="custom-interval" className="text-sm font-medium">
                          Custom Interval (days)
                        </Label>
                        <Input
                          id="custom-interval"
                          type="number"
                          min={1}
                          value={customIntervalDays || ''}
                          onChange={(e) => setCustomIntervalDays(parseInt(e.target.value, 10) || undefined)}
                          placeholder="e.g., 90"
                          className="h-10"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Validity & Expiry */}
              <div className="space-y-4">
                <Label className="text-sm font-medium">Validity & Expiry Settings</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="validity-period" className="text-sm text-muted-foreground">
                      Validity Period (days)
                    </Label>
                    <Input
                      id="validity-period"
                      type="number"
                      min={1}
                      value={validityPeriodDays || ''}
                      onChange={(e) => setValidityPeriodDays(parseInt(e.target.value, 10) || undefined)}
                      placeholder="Leave empty for no expiry"
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      How long before a completed questionnaire expires
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="renewal-reminder" className="text-sm text-muted-foreground">
                      Reminder (days before expiry)
                    </Label>
                    <Input
                      id="renewal-reminder"
                      type="number"
                      min={1}
                      value={renewalReminderDays}
                      onChange={(e) => setRenewalReminderDays(parseInt(e.target.value, 10) || 30)}
                      className="h-10"
                    />
                    <p className="text-xs text-muted-foreground">
                      When to send expiry reminder notifications
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Supplier Targeting Configuration */}
          <Card className="shadow-sm border-gray-200">
            <div className="px-6 py-4 border-b bg-gray-50/80 rounded-t-lg">
              <div className="flex items-center gap-2">
                <Target className="h-5 w-5 text-gray-500" />
                <h2 className="text-base font-semibold text-gray-900">Supplier Targeting</h2>
              </div>
            </div>
            <CardContent className="p-6 space-y-6">
              {/* Target Type Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Target Audience</Label>
                </div>
                <p className="text-xs text-muted-foreground -mt-2">
                  Select which suppliers should receive this questionnaire
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                  {[
                    { value: 'all', label: 'All Suppliers', icon: Users, description: 'Send to all active suppliers' },
                    { value: 'strategic', label: 'Strategic Suppliers', icon: Star, description: 'Preferred/key suppliers only' },
                    { value: 'groups', label: 'Supplier Groups', icon: Building2, description: 'Select specific groups' },
                    { value: 'sites', label: 'Sites', icon: Building2, description: 'Select specific sites' },
                    { value: 'suppliers', label: 'Individual Suppliers', icon: User, description: 'Select specific suppliers' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTargetType(option.value as SupplierTargeting['target_type'])}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        targetType === option.value
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <option.icon className={`h-5 w-5 mb-2 ${targetType === option.value ? 'text-primary' : 'text-gray-400'}`} />
                      <div className={`text-sm font-medium ${targetType === option.value ? 'text-primary' : 'text-gray-700'}`}>
                        {option.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">{option.description}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Conditional Selection Based on Target Type */}
              {targetType === 'groups' && (
                <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                  <Label className="text-sm font-medium">Select Supplier Groups</Label>
                  {loadingTargetOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading groups...
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search groups..."
                          value={targetSearchTerm}
                          onChange={(e) => setTargetSearchTerm(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {availableGroups
                          .filter(g => g.name.toLowerCase().includes(targetSearchTerm.toLowerCase()))
                          .map((group) => (
                            <label
                              key={group.id}
                              className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                            >
                              <Checkbox
                                checked={selectedGroupIds.includes(group.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedGroupIds([...selectedGroupIds, group.id]);
                                  } else {
                                    setSelectedGroupIds(selectedGroupIds.filter(id => id !== group.id));
                                  }
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-700">{group.name}</div>
                                {group.company_count > 0 && (
                                  <div className="text-xs text-muted-foreground">{group.company_count} companies</div>
                                )}
                              </div>
                            </label>
                          ))}
                        {availableGroups.length === 0 && (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">No groups available</div>
                        )}
                      </div>
                      {selectedGroupIds.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedGroupIds.map(id => {
                            const group = availableGroups.find(g => g.id === id);
                            return group ? (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {group.name}
                                <X
                                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                                  onClick={() => setSelectedGroupIds(selectedGroupIds.filter(gid => gid !== id))}
                                />
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {targetType === 'sites' && (
                <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                  <Label className="text-sm font-medium">Select Sites</Label>
                  {loadingTargetOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading sites...
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search sites..."
                          value={targetSearchTerm}
                          onChange={(e) => setTargetSearchTerm(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {availableSites
                          .filter(s => s.name.toLowerCase().includes(targetSearchTerm.toLowerCase()) ||
                                       s.code.toLowerCase().includes(targetSearchTerm.toLowerCase()))
                          .map((site) => (
                            <label
                              key={site.id}
                              className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                            >
                              <Checkbox
                                checked={selectedSiteIds.includes(site.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedSiteIds([...selectedSiteIds, site.id]);
                                  } else {
                                    setSelectedSiteIds(selectedSiteIds.filter(id => id !== site.id));
                                  }
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-700">{site.name}</div>
                                <div className="text-xs text-muted-foreground">{site.code} · {site.city}, {site.country}</div>
                              </div>
                            </label>
                          ))}
                        {availableSites.length === 0 && (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">No sites available</div>
                        )}
                      </div>
                      {selectedSiteIds.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedSiteIds.map(id => {
                            const site = availableSites.find(s => s.id === id);
                            return site ? (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {site.name}
                                <X
                                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                                  onClick={() => setSelectedSiteIds(selectedSiteIds.filter(sid => sid !== id))}
                                />
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {targetType === 'suppliers' && (
                <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                  <Label className="text-sm font-medium">Select Individual Suppliers</Label>
                  {loadingTargetOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading suppliers...
                    </div>
                  ) : (
                    <>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search suppliers..."
                          value={targetSearchTerm}
                          onChange={(e) => setTargetSearchTerm(e.target.value)}
                          className="pl-9 h-9"
                        />
                      </div>
                      <div className="border rounded-lg max-h-48 overflow-y-auto">
                        {availableSuppliers
                          .filter(s => s.company_name?.toLowerCase().includes(targetSearchTerm.toLowerCase()) ||
                                       s.supplier_code?.toLowerCase().includes(targetSearchTerm.toLowerCase()))
                          .map((supplier) => (
                            <label
                              key={supplier.id}
                              className="flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                            >
                              <Checkbox
                                checked={selectedSupplierIds.includes(supplier.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedSupplierIds([...selectedSupplierIds, supplier.id]);
                                  } else {
                                    setSelectedSupplierIds(selectedSupplierIds.filter(id => id !== supplier.id));
                                  }
                                }}
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-700">{supplier.company_name}</div>
                                {supplier.supplier_code && (
                                  <div className="text-xs text-muted-foreground">Code: {supplier.supplier_code}</div>
                                )}
                              </div>
                            </label>
                          ))}
                        {availableSuppliers.length === 0 && (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">No suppliers available</div>
                        )}
                      </div>
                      {selectedSupplierIds.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedSupplierIds.map(id => {
                            const supplier = availableSuppliers.find(s => s.id === id);
                            return supplier ? (
                              <Badge key={id} variant="secondary" className="gap-1">
                                {supplier.company_name}
                                <X
                                  className="h-3 w-3 cursor-pointer hover:text-destructive"
                                  onClick={() => setSelectedSupplierIds(selectedSupplierIds.filter(sid => sid !== id))}
                                />
                              </Badge>
                            ) : null;
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {targetType === 'strategic' && (
                <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                  <Label className="text-sm font-medium">Strategic Suppliers</Label>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Star className="h-5 w-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm text-amber-800 font-medium">
                          Targeting all preferred/strategic suppliers
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          This questionnaire will be automatically distributed to all suppliers marked as "Preferred" or "Strategic" in the system.
                        </p>
                        {!loadingTargetOptions && (
                          <p className="text-xs text-amber-600 mt-2">
                            Currently {availableSuppliers.filter(s => s.is_preferred).length} strategic supplier(s) in the system
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Commodity Codes - Always visible as additional filter */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Commodity Codes (Optional)</Label>
                </div>
                <Input
                  value={commodityCodes}
                  onChange={(e) => setCommodityCodes(e.target.value)}
                  placeholder="e.g., RAW001, PACK002, ELEC003 (comma-separated)"
                  className="h-10"
                />
                <p className="text-xs text-muted-foreground">
                  Additionally filter suppliers by commodity codes. Leave empty to skip this filter.
                </p>
              </div>

              {/* Summary */}
              {targetType !== 'all' && (
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-blue-600 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <span className="font-medium">Targeting Summary: </span>
                      {targetType === 'strategic' && (
                        <span>All strategic/preferred suppliers ({availableSuppliers.filter(s => s.is_preferred).length} total)</span>
                      )}
                      {targetType === 'groups' && selectedGroupIds.length > 0 && (
                        <span>{selectedGroupIds.length} group(s) selected</span>
                      )}
                      {targetType === 'sites' && selectedSiteIds.length > 0 && (
                        <span>{selectedSiteIds.length} site(s) selected</span>
                      )}
                      {targetType === 'suppliers' && selectedSupplierIds.length > 0 && (
                        <span>{selectedSupplierIds.length} supplier(s) selected</span>
                      )}
                      {((targetType === 'groups' && selectedGroupIds.length === 0) ||
                        (targetType === 'sites' && selectedSiteIds.length === 0) ||
                        (targetType === 'suppliers' && selectedSupplierIds.length === 0)) && (
                        <span className="text-amber-700">No selections made yet</span>
                      )}
                      {commodityCodes.trim() && (
                        <span> + commodity filter applied</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Sections & Questions Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <ListChecks className="h-5 w-5 text-primary" />
                Sections & Questions
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Build your questionnaire by adding sections and questions
              </p>
            </div>
            <Button onClick={addSection} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Section
            </Button>
          </div>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, sIdx) => {
              const color = SECTION_COLORS[sIdx % SECTION_COLORS.length];
              const isOpen = openSections[section._key] !== false;

              return (
                <div key={section._key} className={`rounded-xl border-2 ${color.border} overflow-hidden shadow-sm`}>
                  {/* Section Header */}
                  <Collapsible open={isOpen} onOpenChange={() => toggleSection(section._key)}>
                    <CollapsibleTrigger asChild>
                      <div className={`${color.bg} px-5 py-4 cursor-pointer hover:brightness-95 transition-all`}>
                        <div className="flex items-center gap-4">
                          <div className={`flex items-center justify-center h-9 w-9 rounded-lg ${color.badge} font-bold text-sm`}>
                            {sIdx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className={`font-semibold text-base ${color.text}`}>
                                {section.title || `Section ${sIdx + 1}`}
                              </h3>
                              {section.is_required && (
                                <Badge variant="outline" className={`text-xs ${color.text} border-current`}>
                                  Required
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5">
                              <span className="text-sm text-muted-foreground">
                                {section.questions.length} question{section.questions.length !== 1 ? 's' : ''}
                              </span>
                              {section.weight > 0 && (
                                <span className="text-sm text-muted-foreground">
                                  Weight: {section.weight}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); moveSectionUp(sIdx); }}
                                  disabled={sIdx === 0}
                                >
                                  <ChevronUp className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Move up</TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); moveSectionDown(sIdx); }}
                                  disabled={sIdx === sections.length - 1}
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Move down</TooltipContent>
                            </Tooltip>
                            {sections.length > 1 && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                                    onClick={(e) => { e.stopPropagation(); removeSection(section._key); }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Delete section</TooltipContent>
                              </Tooltip>
                            )}
                            <ChevronRight className={`h-5 w-5 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                          </div>
                        </div>
                      </div>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <div className="px-5 py-5 space-y-5 bg-white">
                        {/* Section Settings */}
                        <div className={`rounded-lg border p-4 ${color.bg} bg-opacity-30`}>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Section Title</Label>
                              <Input
                                value={section.title}
                                onChange={(e) => updateSection(section._key, 'title', e.target.value)}
                                placeholder="e.g., Quality Management"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label className="text-sm font-medium">Weight</Label>
                              <Input
                                type="number"
                                min={0}
                                value={section.weight}
                                onChange={(e) => updateSection(section._key, 'weight', parseInt(e.target.value, 10) || 0)}
                              />
                            </div>
                            <div className="flex items-end">
                              <div className="flex items-center gap-2 h-10 px-3 rounded-md border bg-white">
                                <Switch
                                  checked={section.is_required}
                                  onCheckedChange={(v) => updateSection(section._key, 'is_required', v)}
                                />
                                <Label className="text-sm cursor-pointer">Required</Label>
                              </div>
                            </div>
                          </div>
                          {/* Description row below */}
                          <div className="mt-4 space-y-2">
                            <Label className="text-sm font-medium">Description</Label>
                            <Textarea
                              value={section.description}
                              onChange={(e) => updateSection(section._key, 'description', e.target.value)}
                              placeholder="Optional description for this section..."
                              rows={2}
                            />
                          </div>
                        </div>

                        {/* Questions */}
                        <div className="space-y-3">
                          {section.questions.map((question, qIdx) => (
                            <QuestionEditor
                              key={question._key}
                              question={question}
                              qIdx={qIdx}
                              totalQuestions={section.questions.length}
                              sectionKey={section._key}
                              sectionColor={color}
                              onUpdate={updateQuestion}
                              onRemove={removeQuestion}
                              onMoveUp={moveQuestionUp}
                              onMoveDown={moveQuestionDown}
                              onAddChoice={addChoice}
                              onUpdateChoice={updateChoice}
                              onRemoveChoice={removeChoice}
                              errorMessage={
                                invalidQuestionKeys.has(question._key)
                                  ? `Add at least ${MIN_CHOICES_PER_QUESTION} answer options to this question`
                                  : undefined
                              }
                            />
                          ))}
                        </div>

                        {/* Add Question Button */}
                        <button
                          onClick={() => addQuestion(section._key)}
                          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
                        >
                          <Plus className="h-4 w-4" />
                          Add Question
                        </button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              );
            })}
          </div>

          {/* Add Section Button */}
          <button
            onClick={addSection}
            className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl text-base font-medium text-gray-500 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all flex items-center justify-center gap-2"
          >
            <Plus className="h-5 w-5" />
            Add Another Section
          </button>
        </div>
      </div>

      {/* Preview Dialog */}
      <TemplatePreview
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        templateName={name}
        description={description}
        instructions={instructions}
        passScore={passScore}
        sections={sections}
      />
    </TooltipProvider>
  );
}
